import { useState, useEffect, useMemo } from "react";
import { Download, ScanLine, FileJson } from "lucide-react";
import { foApi } from "@/webapp/fleet-owners/api";
import { errorText } from "@/lib/api";
import { PageHead, Panel, Tag, EmptyState, Button, DataTable, Td, Select, Notice, downloadFile, toCsv, when } from "@/webapp/fleet-owners/ui";

const OUT_TONE = { entry: "ok", exit: "info", not_onsite: "warn", already_onsite: "warn", duplicate: "mute", dismissed: "mute" };
const siteName = (s) => String(s || "").replace(/^./, (c) => c.toUpperCase());
const COLS = [
  ["time_utc", (e) => e.created_at], ["plate", (e) => e.plate], ["vehicle", (e) => e.vehicle], ["booking", (e) => e.booking_ref], ["site", (e) => e.site],
  ["stall", (e) => e.stall], ["lane", (e) => e.direction], ["outcome", (e) => e.outcome], ["source", (e) => e.source], ["confidence", (e) => e.confidence],
  ["read_id", (e) => e.id], ["prev_hash", (e) => e.prev_hash], ["hash", (e) => e.hash],
];

export default function GateProof() {
  const [res, setRes] = useState(null);
  const [err, setErr] = useState("");
  const [vehicle, setVehicle] = useState("");
  const [site, setSite] = useState("");
  useEffect(() => { foApi.gateProof().then(setRes).catch((e) => setErr(errorText(e, "Could not load gate proof."))); }, []);
  const events = useMemo(() => (res?.events || []).filter((e) => (!vehicle || e.vehicle === vehicle) && (!site || e.site === site)), [res, vehicle, site]);
  const vehicles = [...new Set((res?.events || []).map((e) => e.vehicle))];
  const sites = [...new Set((res?.events || []).map((e) => e.site))];
  const stamp = new Date().toISOString().slice(0, 19).replace(/[:T]/g, "-");
  const csv = () => downloadFile(`fleetpods-gate-proof-${stamp}.csv`, toCsv(events, COLS));
  const json = () => downloadFile(`fleetpods-gate-proof-${stamp}.json`, JSON.stringify({ generated_at: res.generated_at, filters: { vehicle, site }, events }, null, 2), "application/json");
  return (
    <>
      <PageHead eyebrow="Gate Proof" title="Verified arrivals & departures" sub="Every plate read at a FleetPods gate for your bookings. Each record is SHA-256 chained to the one before it, so any edit is detectable. Use it as evidence for detention and disputes.">
        <Button testid="fo-gate-csv" onClick={csv} disabled={!events.length}><Download className="h-4 w-4" />CSV</Button>
        <Button testid="fo-gate-json" onClick={json} disabled={!events.length}><FileJson className="h-4 w-4" />JSON proof</Button>
      </PageHead>
      {err && <Notice testid="fo-gate-error">{err}</Notice>}
      {!res && !err && <p className="text-sm text-slate-400" data-testid="fo-gate-loading">Loading gate events…</p>}
      {res && (
        <Panel testid="fo-gate-panel" className="fo-rise fo-rise-1" title={`${events.length} event${events.length === 1 ? "" : "s"}`} action={
          <div className="flex gap-2">
            <div className="w-40"><Select data-testid="fo-gate-filter-vehicle" value={vehicle} onChange={(e) => setVehicle(e.target.value)}><option value="">All vehicles</option>{vehicles.map((v) => <option key={v} value={v}>{v}</option>)}</Select></div>
            <div className="w-36"><Select data-testid="fo-gate-filter-site" value={site} onChange={(e) => setSite(e.target.value)}><option value="">All sites</option>{sites.map((s) => <option key={s} value={s}>{siteName(s)}</option>)}</Select></div>
          </div>}>
          {events.length ? (
            <DataTable head={["Time", "Plate", "Vehicle", "Booking", "Site · stall", "Lane", "Outcome", "Audit hash"]} testid="fo-gate-table">
              {events.map((e) => (
                <tr key={e.id} data-testid={`fo-gate-row-${e.id}`}>
                  <Td mono>{when(e.created_at)}</Td>
                  <Td mono strong><span className="tracking-wider">{e.plate}</span></Td>
                  <Td>{e.vehicle}</Td>
                  <Td mono>{e.booking_ref}</Td>
                  <Td>{siteName(e.site)} · <span className="font-mono text-xs">{e.stall}</span></Td>
                  <Td>{e.direction}</Td>
                  <Td><Tag tone={OUT_TONE[e.outcome] || "mute"}>{String(e.outcome).replace(/_/g, " ")}</Tag></Td>
                  <Td mono><span title={e.hash} className="text-slate-400">{String(e.hash).slice(0, 16)}…</span></Td>
                </tr>
              ))}
            </DataTable>
          ) : <div className="p-5"><EmptyState icon={ScanLine} testid="fo-gate-empty" title="No gate events yet" body="Add plates to your vehicles and book FleetPods parking. Arrivals and departures recorded by gate cameras appear here automatically." /></div>}
        </Panel>
      )}
    </>
  );
}
